import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ReceiptText } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { customersService } from '@/services/customers';
import { invoicesService, type GenerateInvoicePayload } from '@/services/invoices';
import type { SalesOrder } from '@/types/orders';

interface Props {
  order: SalesOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Compact two-section form: pick billing/shipping addresses and (optionally)
 * date / terms / notes. Hits POST /invoices which generates the invoice
 * with full GST split and snapshots the addresses + seller block at issue
 * time. The created invoice is immutable; cancellation is a separate flow.
 */
export function GenerateInvoiceDialog({ order, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [billingAddressId, setBillingAddressId] = useState<string>('__default');
  const [shippingAddressId, setShippingAddressId] = useState<string>('__default');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');

  // Pull customer addresses so the operator can pick / override.
  const addressesQuery = useQuery({
    queryKey: ['customers', 'addresses', order?.customerId],
    queryFn: () => customersService.listAddresses(order!.customerId),
    enabled: !!order && open,
  });
  const addresses = addressesQuery.data ?? [];
  const defaultBilling = addresses.find((a) => a.isDefaultBilling) ?? null;
  const defaultShipping = addresses.find((a) => a.isDefaultShipping) ?? null;

  // GST classification preview — intra vs inter — based on currently
  // selected billing address vs the (placeholder) "warehouse state".
  const selectedBilling =
    billingAddressId === '__default'
      ? defaultBilling
      : addresses.find((a) => a.id === billingAddressId) ?? null;
  // We don't have direct access to the warehouse state on the FE order
  // payload yet, so the preview just shows the billing state for context.
  const billingState = selectedBilling?.state ?? '—';

  useEffect(() => {
    if (!open) return;
    setBillingAddressId('__default');
    setShippingAddressId('__default');
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setDueDate('');
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: GenerateInvoicePayload = {
        salesOrderId: order!.id,
        invoiceDate: invoiceDate || undefined,
        dueDate: dueDate || undefined,
        billingAddressId:
          billingAddressId === '__default' ? undefined : billingAddressId,
        shippingAddressId:
          shippingAddressId === '__default' ? undefined : shippingAddressId,
      };
      return invoicesService.generate(payload);
    },
    onSuccess: (invoice) => {
      toast.success(`Invoice ${invoice.invoiceNumber} issued`);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'forOrder', order?.id] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not generate invoice'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!order) return;
    mutation.mutate();
  };

  const customerName = order?.customer.name ?? '';

  // Both addresses must resolve to something or the BE will reject.
  const noBilling = billingAddressId === '__default' && !defaultBilling;
  const noShipping = shippingAddressId === '__default' && !defaultShipping;
  const blocked = noBilling || noShipping;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <ReceiptText className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>Generate invoice · {order?.orderNumber}</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Issue tax invoice for {customerName}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Tax splits compute at issue time using each item's GST rate +
              warehouse state vs billing state. Invoices are immutable once
              ISSUED; cancel + re-generate to correct mistakes.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel="Generate invoice"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="3xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Bill to" htmlFor="billing" required>
          <Select value={billingAddressId} onValueChange={setBillingAddressId}>
            <SelectTrigger id="billing" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default">
                {defaultBilling
                  ? `Default · ${defaultBilling.label || defaultBilling.city}`
                  : 'Default — none configured'}
              </SelectItem>
              {addresses.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span>{a.label || a.city}</span>
                  <span className="ml-2 text-muted-foreground">
                    {a.city}, {a.state}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Ship to" htmlFor="shipping" required>
          <Select value={shippingAddressId} onValueChange={setShippingAddressId}>
            <SelectTrigger id="shipping" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default">
                {defaultShipping
                  ? `Default · ${defaultShipping.label || defaultShipping.city}`
                  : 'Default — none configured'}
              </SelectItem>
              {addresses.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span>{a.label || a.city}</span>
                  <span className="ml-2 text-muted-foreground">
                    {a.city}, {a.state}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[11.5px] text-muted-foreground">
        Billing state: <span className="font-mono text-foreground">{billingState}</span>
        {' · '}
        GST classification computed at issue time against warehouse{' '}
        <span className="font-mono text-foreground">{order?.warehouse.code}</span>.
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Invoice date" htmlFor="invoiceDate">
          <Input
            id="invoiceDate"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="h-9"
          />
        </FormField>
        <FormField label="Due date" htmlFor="dueDate" hint="Optional — payable on receipt if blank.">
          <Input
            id="dueDate"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="h-9"
          />
        </FormField>
      </div>

      {blocked && (
        <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
          {customerName} has no{' '}
          {noBilling && noShipping
            ? 'billing or shipping address'
            : noBilling
              ? 'default billing address'
              : 'default shipping address'}{' '}
          configured. Add one in the customer detail Addresses tab before invoicing.
        </div>
      )}
    </FormSheet>
  );
}

